import { useEffect, useRef } from 'react'
import { sendGAEvent, sendGAPageView } from './useGoogleAnalytics'

/**
 * Track performance metrics and SPA pageviews (same pattern as contract-frontend).
 * @param {string} pathname - Current route pathname
 * @param {boolean} isGAInitialized - Whether Google Analytics is initialized
 * @param {Object} options - Optional parameters like user_id, uid for pageview tracking
 */
export const usePerformanceTracking = (pathname, isGAInitialized, options = {}) => {
  const previousPathname = useRef(pathname)
  const isInitialMount = useRef(true)

  useEffect(() => {
    if (!isGAInitialized) return

    const trackSPARouteLoadTime = () => {
      const routeStartTime = performance.now()
      performance.mark(`route-start-${pathname}`)

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const routeEndTime = performance.now()
          const routeLoadTime = Math.round(routeEndTime - routeStartTime)

          try {
            performance.mark(`route-end-${pathname}`)
            performance.measure(`route-${pathname}`, `route-start-${pathname}`, `route-end-${pathname}`)
          } catch (e) {
            // Ignore if marks don't exist
          }

          sendGAEvent('spa_route_load_time', {
            metric_name: 'spa_route_load_time',
            metric_value: routeLoadTime,
            page_path: pathname,
          })
        })
      })
    }

    const trackInitialPageLoadTime = () => {
      setTimeout(() => {
        const [nav] = performance.getEntriesByType('navigation')
        if (nav) {
          const pageLoadTime = Math.round(nav.loadEventEnd - nav.fetchStart)

          sendGAEvent('initial_page_load_time', {
            metric_name: 'initial_page_load_time',
            metric_value: pageLoadTime,
            page_path: pathname,
          })
        }
      }, 0)
    }

    const isRouteChange = previousPathname.current !== pathname

    if (isInitialMount.current) {
      isInitialMount.current = false
      sendGAPageView(pathname, options)
      if (document.readyState === 'complete') {
        trackInitialPageLoadTime()
      } else {
        window.addEventListener('load', trackInitialPageLoadTime, { once: true })
      }
      previousPathname.current = pathname
    } else if (isRouteChange) {
      trackSPARouteLoadTime()
      sendGAPageView(pathname, options)
      previousPathname.current = pathname
    }
  }, [pathname, isGAInitialized, options.user_id, options.uid])
}
